"use client";

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { WidgetPortal } from './WidgetPortal';
import type { Annotation, AnnotationTool } from '../types';
import { generateId, cn } from '../utils';
import {
  PencilIcon,
  SquareIcon,
  CircleIcon,
  ArrowRightIcon,
  TypeIcon,
  UndoIcon,
  CloseIcon,
  CheckIcon,
} from './Icons';

interface AnnotationOverlayProps {
  screenshot: string;
  onComplete: (annotations: Annotation[], screenshot: string) => void;
  onCancel: () => void;
}

const tools: { id: AnnotationTool; icon: React.FC<any>; label: string }[] = [
  { id: 'pen', icon: PencilIcon, label: 'Draw' },
  { id: 'rectangle', icon: SquareIcon, label: 'Rectangle' },
  { id: 'circle', icon: CircleIcon, label: 'Circle' },
  { id: 'arrow', icon: ArrowRightIcon, label: 'Arrow' },
  { id: 'text', icon: TypeIcon, label: 'Text' },
];

const colors = [
  '#EF4444', // red
  '#F97316', // orange
  '#FBBF24', // yellow
  '#22C55E', // green
  '#3B82F6', // blue
  '#8B5CF6', // purple
  '#000000', // black
];

export const AnnotationOverlay: React.FC<AnnotationOverlayProps> = ({
  screenshot,
  onComplete,
  onCancel,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeTool, setActiveTool] = useState<AnnotationTool>('pen');
  const [activeColor, setActiveColor] = useState('#EF4444');
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPath, setCurrentPath] = useState<{ x: number; y: number }[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [textInput, setTextInput] = useState('');
  const [textPosition, setTextPosition] = useState<{ x: number; y: number } | null>(null);

  const imgRef = useRef<HTMLImageElement | null>(null);
  // The canvas works at the screenshot's own resolution and is scaled down
  // for display. It used to be sized to the display box, so the exported,
  // annotated shot was the size of the preview — a sharp retina capture came
  // back blurry. Strokes and text are scaled by this factor so they look the
  // same thickness on screen as before.
  const [display, setDisplay] = useState({ width: 0, height: 0 });
  const strokeScale = display.width ? (imgRef.current?.naturalWidth ?? display.width) / display.width : 1;

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      setImageLoaded(true);
    };
    img.src = screenshot;
  }, [screenshot]);


  const redrawCanvas = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number) => {
      if (!imgRef.current) return;

      ctx.clearRect(0, 0, width, height);
      ctx.drawImage(imgRef.current, 0, 0, width, height);

      // Draw all annotations
      annotations.forEach((annotation) => {
        ctx.strokeStyle = annotation.color;
        ctx.fillStyle = annotation.color;
        ctx.lineWidth = 3 * strokeScale;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        switch (annotation.type) {
          case 'pen':
            if (annotation.path && annotation.path.length > 0) {
              ctx.beginPath();
              ctx.moveTo(annotation.path[0].x * width, annotation.path[0].y * height);
              annotation.path.forEach((point) => {
                ctx.lineTo(point.x * width, point.y * height);
              });
              ctx.stroke();
            }
            break;

          case 'rectangle':
            if (annotation.startX !== undefined && annotation.startY !== undefined && 
                annotation.endX !== undefined && annotation.endY !== undefined) {
              const x = annotation.startX * width;
              const y = annotation.startY * height;
              const w = (annotation.endX - annotation.startX) * width;
              const h = (annotation.endY - annotation.startY) * height;
              ctx.strokeRect(x, y, w, h);
            }
            break;

          case 'circle':
            if (annotation.startX !== undefined && annotation.startY !== undefined && 
                annotation.endX !== undefined && annotation.endY !== undefined) {
              const centerX = (annotation.startX + annotation.endX) / 2 * width;
              const centerY = (annotation.startY + annotation.endY) / 2 * height;
              const radiusX = Math.abs(annotation.endX - annotation.startX) / 2 * width;
              const radiusY = Math.abs(annotation.endY - annotation.startY) / 2 * height;
              ctx.beginPath();
              ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
              ctx.stroke();
            }
            break;

          case 'arrow':
            if (annotation.startX !== undefined && annotation.startY !== undefined && 
                annotation.endX !== undefined && annotation.endY !== undefined) {
              const fromX = annotation.startX * width;
              const fromY = annotation.startY * height;
              const toX = annotation.endX * width;
              const toY = annotation.endY * height;

              const headLength = 15 * strokeScale;
              const angle = Math.atan2(toY - fromY, toX - fromX);

              ctx.beginPath();
              ctx.moveTo(fromX, fromY);
              ctx.lineTo(toX, toY);
              ctx.stroke();

              ctx.beginPath();
              ctx.moveTo(toX, toY);
              ctx.lineTo(
                toX - headLength * Math.cos(angle - Math.PI / 6),
                toY - headLength * Math.sin(angle - Math.PI / 6)
              );
              ctx.lineTo(
                toX - headLength * Math.cos(angle + Math.PI / 6),
                toY - headLength * Math.sin(angle + Math.PI / 6)
              );
              ctx.closePath();
              ctx.fill();
            }
            break;

          case 'text':
            if (annotation.text && annotation.startX !== undefined && annotation.startY !== undefined) {
              ctx.font = `bold ${Math.round(16 * strokeScale)}px sans-serif`;
              ctx.fillText(annotation.text, annotation.startX * width, annotation.startY * height);
            }
            break;
        }
      });
    },
    [annotations, strokeScale]
  );

  useEffect(() => {
    if (!imageLoaded || !canvasRef.current || !imgRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    const imgRatio = imgRef.current.width / imgRef.current.height;
    const containerRatio = containerWidth / containerHeight;

    let width: number, height: number;

    if (imgRatio > containerRatio) {
      width = containerWidth;
      height = containerWidth / imgRatio;
    } else {
      height = containerHeight;
      width = containerHeight * imgRatio;
    }

    const img = imgRef.current;
    canvas.width = img.naturalWidth || width;
    canvas.height = img.naturalHeight || height;
    if (display.width !== width || display.height !== height) {
      setDisplay({ width, height });
    }

    redrawCanvas(ctx, canvas.width, canvas.height);
  }, [imageLoaded, annotations, display.width, display.height, redrawCanvas]);

  // Pointer events cover mouse, touch and pen alike; annotation used to be
  // mouse-only, so it did nothing on a tablet.
  const getCanvasPoint = (e: React.PointerEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const { clientX, clientY } = e;

    return {
      x: (clientX - rect.left) / rect.width,
      y: (clientY - rect.top) / rect.height,
    };
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    // Keep receiving moves when a finger or the mouse strays off the canvas.
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    const point = getCanvasPoint(e);

    if (activeTool === 'text') {
      setTextPosition({ x: point.x, y: point.y });
      return;
    }

    setIsDrawing(true);
    setStartPoint(point);

    if (activeTool === 'pen') {
      setCurrentPath([point]);
    }
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const point = getCanvasPoint(e);

    if (activeTool === 'pen') {
      setCurrentPath((prev) => [...prev, point]);

      // Draw current stroke
      redrawCanvas(ctx, canvas.width, canvas.height);
      ctx.strokeStyle = activeColor;
      ctx.lineWidth = 3 * strokeScale;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      const allPoints = [...currentPath, point];
      if (allPoints.length > 0) {
        ctx.moveTo(allPoints[0].x * canvas.width, allPoints[0].y * canvas.height);
        allPoints.forEach((p) => {
          ctx.lineTo(p.x * canvas.width, p.y * canvas.height);
        });
        ctx.stroke();
      }
    } else if (startPoint) {
      // Draw preview for shapes
      redrawCanvas(ctx, canvas.width, canvas.height);
      ctx.strokeStyle = activeColor;
      ctx.fillStyle = activeColor;
      ctx.lineWidth = 3 * strokeScale;
      ctx.lineCap = 'round';

      const width = canvas.width;
      const height = canvas.height;

      switch (activeTool) {
        case 'rectangle': {
          const x = startPoint.x * width;
          const y = startPoint.y * height;
          const w = (point.x - startPoint.x) * width;
          const h = (point.y - startPoint.y) * height;
          ctx.strokeRect(x, y, w, h);
          break;
        }
        case 'circle': {
          const centerX = (startPoint.x + point.x) / 2 * width;
          const centerY = (startPoint.y + point.y) / 2 * height;
          const radiusX = Math.abs(point.x - startPoint.x) / 2 * width;
          const radiusY = Math.abs(point.y - startPoint.y) / 2 * height;
          ctx.beginPath();
          ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
          ctx.stroke();
          break;
        }
        case 'arrow': {
          const fromX = startPoint.x * width;
          const fromY = startPoint.y * height;
          const toX = point.x * width;
          const toY = point.y * height;
          const headLength = 15 * strokeScale;
          const angle = Math.atan2(toY - fromY, toX - fromX);

          ctx.beginPath();
          ctx.moveTo(fromX, fromY);
          ctx.lineTo(toX, toY);
          ctx.stroke();

          ctx.beginPath();
          ctx.moveTo(toX, toY);
          ctx.lineTo(
            toX - headLength * Math.cos(angle - Math.PI / 6),
            toY - headLength * Math.sin(angle - Math.PI / 6)
          );
          ctx.lineTo(
            toX - headLength * Math.cos(angle + Math.PI / 6),
            toY - headLength * Math.sin(angle + Math.PI / 6)
          );
          ctx.closePath();
          ctx.fill();
          break;
        }
      }
    }
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!isDrawing || !startPoint) return;
    e.preventDefault();

    const point = getCanvasPoint(e);

    const newAnnotation: Annotation = {
      id: generateId(),
      type: activeTool,
      color: activeColor,
    };

    if (activeTool === 'pen') {
      newAnnotation.path = [...currentPath, point];
    } else {
      newAnnotation.startX = startPoint.x;
      newAnnotation.startY = startPoint.y;
      newAnnotation.endX = point.x;
      newAnnotation.endY = point.y;
    }

    setAnnotations((prev) => [...prev, newAnnotation]);
    setIsDrawing(false);
    setCurrentPath([]);
    setStartPoint(null);
  };

  const handleTextSubmit = () => {
    if (!textPosition || !textInput.trim()) {
      setTextPosition(null);
      setTextInput('');
      return;
    }

    const newAnnotation: Annotation = {
      id: generateId(),
      type: 'text',
      color: activeColor,
      startX: textPosition.x,
      startY: textPosition.y,
      text: textInput.trim(),
    };

    setAnnotations((prev) => [...prev, newAnnotation]);
    setTextPosition(null);
    setTextInput('');
  };

  const handleUndo = () => {
    setAnnotations((prev) => prev.slice(0, -1));
  };

  const handleComplete = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL('image/png');
    onComplete(annotations, dataUrl);
  };

  return <WidgetPortal>
    <div className="fixed inset-0 z-[1000000] bg-black/90 flex flex-col min-h-screen">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-center gap-3 p-4 bg-white/95 backdrop-blur-md border-b border-gray-200 shadow-lg flex-shrink-0">
        {/* Tools */}
        <div className="flex items-center gap-1.5 bg-gray-100 p-1.5 rounded-xl">
          {tools.map((tool) => (
            <button
              type="button"
              key={tool.id}
              onClick={() => setActiveTool(tool.id)}
              title={tool.label}
              aria-label={tool.label}
              aria-pressed={activeTool === tool.id}
              className={cn(
                'p-2.5 rounded-lg border-none cursor-pointer transition-all',
                activeTool === tool.id
                  ? 'bg-matter-ink text-matter-cream shadow-lg shadow-matter-ink/30'
                  : 'bg-transparent text-gray-600 hover:text-gray-900 hover:bg-white'
              )}
            >
              <tool.icon className="w-5 h-5" />
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-gray-300" />

        {/* Colors */}
        <div className="flex items-center gap-2 bg-gray-100 p-1.5 rounded-xl">
          {colors.map((color) => (
            <button
              type="button"
              key={color}
              aria-label={`Colour ${color}`}
              aria-pressed={activeColor === color}
              onClick={() => setActiveColor(color)}
              className={cn(
                'w-8 h-8 rounded-full border-2 cursor-pointer transition-all overflow-hidden p-0',
                activeColor === color
                  ? 'border-gray-900 scale-110 shadow-lg'
                  : 'border-white hover:scale-105 shadow-md'
              )}
            >
              {/* The swatch is a child: the widget's button reset overrides
                  an inline background on the button itself. */}
              <span className="block w-full h-full" style={{ backgroundColor: color }} />
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-gray-300" />

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleUndo}
            disabled={annotations.length === 0}
            className="p-2.5 rounded-xl border-none cursor-pointer transition-all bg-gray-100 text-gray-600 hover:text-gray-900 hover:bg-gray-200 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            title="Undo"
          >
            <UndoIcon className="w-5 h-5" />
          </button>
          <button
            onClick={onCancel}
            className="p-2.5 rounded-xl border-none cursor-pointer transition-all bg-red-50 text-red-600 hover:bg-red-100 hover:shadow-md"
            title="Cancel"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
          <button
            onClick={handleComplete}
            className="px-4 py-2.5 rounded-xl border-none cursor-pointer transition-all bg-green-500 text-white font-semibold hover:bg-green-600 hover:shadow-lg shadow-md"
            title="Done"
          >
            <CheckIcon className="w-5 h-5 inline mr-1" /> Done
          </button>
        </div>
      </div>

      {/* Canvas Container */}
      <div
        ref={containerRef}
        className="flex-1 flex items-center justify-center p-4 min-h-0"
      >
        {imageLoaded && (
          <canvas
            ref={canvasRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            // A class, not an inline style: the scoped `* { cursor: auto }`
            // reset is !important and would beat an inline cursor.
            className={cn('rounded-lg shadow-2xl', activeTool === 'text' ? 'cursor-text' : 'cursor-crosshair')}
            style={{
              width: display.width || undefined,
              height: display.height || undefined,
              // Without this the browser scrolls or zooms instead of drawing.
              touchAction: 'none',
            }}
          />
        )}

        {/* Text Input */}
        {textPosition && (
          <div 
            className="absolute z-10"
            style={{
              left: `calc(50% + ${(textPosition.x - 0.5) * display.width}px)`,
              top: `calc(50% + ${(textPosition.y - 0.5) * display.height}px)`,
            }}
          >
            <input
              type="text"
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTextSubmit();
                if (e.key === 'Escape') {
                  setTextPosition(null);
                  setTextInput('');
                }
              }}
              onBlur={handleTextSubmit}
              autoFocus
              placeholder="Enter text..."
              className="px-2 py-1 border-2 rounded text-sm! min-w-[150px] focus:outline-none"
              style={{ borderColor: activeColor, color: activeColor }}
            />
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="text-center py-2 text-gray-500 text-xs! flex-shrink-0">
        {activeTool === 'text' 
          ? 'Tap or click to add text'
          : 'Drag to annotate • Use the tools above to switch modes'}
      </div>
    </div></WidgetPortal>;
};

export default AnnotationOverlay;
